import React, { memo, useMemo, useEffect, useState } from 'react'

import CampaignQuoteView from '../component/CampaignQuoteView'
import SimpleTableTable from '../../SimpleTableTable'
import {
  getTableHeaders,
  getSortableColumns,
  getColumnsFromMetrics,
} from '../settings'
import {
  mapRawDataToTableColumns,
  getFooterData,
  aggregateMissionData,
  formatTableColumn,
  fetchMissionData,
  initFileDownload,
} from '../util'
import { convertArrayToHashMap } from '../../../shared/libraries/dataSets'
import {
  CAMPAIGN_ENUM_TYPES,
  PAYMENT_SYSTEM_TYPES_ENUM,
} from '../../../shared/constants'
import PaymentSystemSelector from '../../PaymentSystemSelector'
import SubmitButton from '../../Form/SubmitButton/index'
import { generateCampaignSummary } from '../../../Containers/Form/Campaigns/CreateEditCampaign/util'
import { convertMonetaryDollarStringToCentValue } from '../../../Utils/Formatters'
import { CONFIG } from '../../../Config/config'
import { convertCampaignDetailsToURLString } from '../../../Containers/Campaigns/CreateEditCampaign/util'
import ExportComponentAsPDF from '../../../Utils/ExportComponentAsPDF'
import CampaignDetails from '../../../Containers/Form/Campaigns/CreateEditCampaign/CampaignDetails'

let requestId = null

const CampaignQuote = ({
  cities,
  overlays,
  printingCostPerFlyer,
  baRedemptionPeriod,
  bonusEnabled,
  bonusLimit,
  payoutPerConversion,
  country,
  metrics,
  hideColumns = ['Total Packs'],
  onDataLoaded,
  simpleView = false,
}) => {
  const [rowsData, setRowsData] = useState([])
  const [processedFeatures, setProcessedFeatures] = useState([])
  const [loading, setLoading] = useState(true)
  const [aggregatedData, setAggregatedData] = useState([])
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)

  const { code: countryCode, currency: currencySign } = country

  const [paymentState, setPaymentState] = useState({
    paymentSystem: PAYMENT_SYSTEM_TYPES_ENUM.PER_MISSION,
    hourlyPayout: null,
    clientMarginPercent: null,
    distributionCost: null,
  })
  if (typeof printingCostPerFlyer == 'string')
    throw new Error(
      'Numeric value expected for printingCostPerFlyer, received string.',
    )
  if (typeof distributionCost == 'string')
    throw new Error(
      'Numeric value expected for distributionCost, received string.',
    )
  if (typeof bonusLimit == 'string') {
    throw new Error('Numeric value expected for bonusLimit, received string.')
  }
  if (typeof payoutPerConversion == 'string')
    throw new Error(
      'Numeric value expected for payoutPerConversion, received string.',
    )

  useEffect(() => {
    if (overlays && overlays.length > 0 && cities && cities.length > 0) {
      updateAggregatedData({
        overlays,
        cities,
        countryCode,
      })
    } else {
      if (loading) setLoading(false)
      if (overlays.length === 0) {
        setProcessedFeatures([])
        setAggregatedData([])
      }
    }
  }, [cities, overlays, countryCode])

  const updateAggregatedData = async ({ overlays, cities, countryCode }) => {
    const existingOverlaysIds = overlays.map(({ id }) => id)
    const existingFeaturesIds = processedFeatures.map(
      ({ properties }) => properties.id,
    )

    const newOverlays = existingFeaturesIds.length
      ? overlays.filter(({ id }) => !existingFeaturesIds.includes(id))
      : overlays

    const currentRequestId = Date.now()
    requestId = currentRequestId

    setLoading(true)

    const citiesMap = convertArrayToHashMap(cities, 'id')

    const newFeatures = await fetchMissionData({
      overlays: newOverlays,
      countryCode,
      citiesMap,
    })

    const nextAggregatedData = await aggregateMissionData(
      [
        // filter out deleted items
        ...processedFeatures,
        ...newFeatures,
      ].filter(({ properties }) => existingOverlaysIds.includes(properties.id)),
    )

    if (currentRequestId !== requestId) {
      //Race condition occurred
      return
    }

    setProcessedFeatures(processedFeatures => [
      ...processedFeatures,
      ...newFeatures,
    ])
    setAggregatedData(nextAggregatedData)
    setLoading(false)
  }

  useEffect(() => {
    const rowsData = mapRawDataToTableColumns({
      data: aggregatedData,
      printingCostPerFlyer: printingCostPerFlyer / 100,
      metrics,
      bonusEnabled,
      payoutPerConversion,
      paymentState,
    })
    setRowsData(rowsData)
  }, [
    aggregatedData,
    printingCostPerFlyer,
    paymentState,
    bonusEnabled,
    bonusLimit,
    payoutPerConversion,
    countryCode,
    metrics,
  ])

  useEffect(() => {
    if (!loading && typeof onDataLoaded === 'function') onDataLoaded()
  }, [loading])

  /**
   * Dynamically generates table's headers in order to show metrics in the table
   */
  const { tableHeaders, sortableColumns } = useMemo(() => {
    const dynamicColumns = getColumnsFromMetrics(metrics || [])
    return {
      tableHeaders: getTableHeaders(dynamicColumns, hideColumns),
      sortableColumns: getSortableColumns(dynamicColumns, hideColumns),
    }
  }, [metrics])
  /**
   * Uses function composition to pass bonus info to getFooterData
   * @param {array} data Table data
   */
  const footerRender = data =>
    getFooterData({
      data,
      bonusEnabled,
      bonusLimit,
      currencySign,
    })
  const postRowRendererWrapper = row => formatTableColumn({ row, currencySign })
  const setPaymentStateHandler = (name, value) => {
    setPaymentState(state => ({ ...state, [name]: value }))
  }

  const onExport = () => {
    const footerData = getFooterData({
      data: rowsData,
      bonusEnabled,
      bonusLimit,
      currencySign,
    })
    initFileDownload(tableHeaders, rowsData, footerData, currencySign)
  }

  const onExportDetailsClick = async () => {
    try {
      setIsSummaryLoading(true)
      const citiesGenerated = await generateCampaignSummary({
        countryCode: country.code,
        overlays: overlays,
        cities,
      })
      // campaignsOverlays is new and existing missions
      const missions = (overlays || []).map(overlay => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: overlay.boundary.map(dto => [
            dto.longitude,
            dto.latitude,
          ]),
        },
      }))
      const campaignDetails = {
        type: CAMPAIGN_ENUM_TYPES.LETTERBOX,
        printingCostPerFlyer,
        redemptionTime: baRedemptionPeriod,
        distributionCost: paymentState.distributionCost
          ? convertMonetaryDollarStringToCentValue(
              paymentState.distributionCost,
            )
          : 0,
        bonusLimit: bonusLimit,
        payoutPerConversion: payoutPerConversion,
        missions,
        countryId: country.id,
      }

      var quoteURL = CONFIG.CAMPAIGN_BUILDER_URL.replace(
        'QUOTE_ID',
        convertCampaignDetailsToURLString(campaignDetails),
      )

      await ExportComponentAsPDF({
        renderContent: () => (
          <CampaignDetails
            countryCode={country.code}
            currencySign={country.currency}
            printingCostPerFlyer={campaignDetails.printingCostPerFlyer}
            distributionCost={campaignDetails.distributionCost}
            bonusCost={campaignDetails.bonusLimit}
            cities={citiesGenerated}
            quoteURL={quoteURL.length <= 8192 ? quoteURL : undefined}
            campaignName={name}
          />
        ),
        delayedRender: false,
        title: 'Campaign summary',
      })
    } finally {
      setIsSummaryLoading(false)
    }
  }
  return (
    <CampaignQuoteView simpleView={simpleView} loading={loading}>
      <div className="buttons">
        <SubmitButton
          disabled={isSummaryLoading}
          title={'Export Quote'}
          className={isSummaryLoading ? 'transparent' : ''}
          type="Button"
          value={isSummaryLoading ? 'Loading...' : 'Export Quote'}
          onClick={onExportDetailsClick}
        />
        <SubmitButton value={'Export data in .csv'} onClick={onExport} />
      </div>
      <br />
      <PaymentSystemSelector
        paymentState={paymentState}
        onChange={setPaymentStateHandler}
      />
      <SimpleTableTable
        header={tableHeaders}
        data={rowsData}
        sortOn={sortableColumns}
        footerRender={footerRender}
        showPages={false}
        postRowRender={postRowRendererWrapper}
      />
    </CampaignQuoteView>
  )
}

export default memo(CampaignQuote)



// WEBPACK FOOTER //
// ./src/Components/CampaignQuote/container/CampaignQuote.jsx